import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Divider,
  Button,
  Tooltip,
  CircularProgress,
  Alert,
  Avatar
} from '@mui/material';
import {
  Close as CloseIcon,
  MarkEmailRead as MarkReadIcon,
  Article as ArticleIcon,
  Campaign as CampaignIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { useNotificationContext } from '../contexts/NotificationContext';
import {
  useUserNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  DocumentUpdateNotification
} from '../hooks/useNotifications';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';

const DRAWER_WIDTH = 400;

export const NotificationDrawer: React.FC = () => {
  const { isNotificationDrawerOpen, closeNotificationDrawer } = useNotificationContext();
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  
  const { data, loading, error, refetch } = useUserNotifications({
    limit: 50,
    unreadOnly: showUnreadOnly
  });
  
  const [markAsRead] = useMarkNotificationRead();
  const [markAllAsRead] = useMarkAllNotificationsRead();

  const notifications = data?.userNotifications?.nodes || [];
  const unreadCount = data?.userNotifications?.unreadCount || 0;

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await markAsRead({
        variables: { input: { notificationId } }
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const formatNotificationTime = (createdAt: string) => {
    return formatDistanceToNow(new Date(createdAt), {
      addSuffix: true,
      locale: de
    });
  };

  const getNotificationIcon = (notification: DocumentUpdateNotification) => {
    const isSystemNotification = notification.changesSummary?.isSystemNotification;
    
    if (isSystemNotification) {
      return <CampaignIcon color="info" />;
    }
    
    return <ArticleIcon color={notification.updateType === 'MAJOR' ? 'warning' : 'primary'} />;
  };

  const getNotificationTitle = (notification: DocumentUpdateNotification) => {
    const isSystemNotification = notification.changesSummary?.isSystemNotification;
    
    if (isSystemNotification) {
      return notification.changesSummary?.title || 'System-Benachrichtigung';
    }
    
    return `${notification.document.reference} aktualisiert`;
  };

  const getNotificationDescription = (notification: DocumentUpdateNotification) => {
    const isSystemNotification = notification.changesSummary?.isSystemNotification;
    
    if (isSystemNotification) {
      return notification.changesSummary?.description || 'Neue Systemankündigung verfügbar';
    }
    
    const updateTypeText = notification.updateType === 'MAJOR' ? 'Wichtige Änderung' : 'Aktualisierung';
    return `${updateTypeText} von Version ${notification.previousVersion} auf ${notification.newVersion}`;
  };

  return (
    <Drawer
      anchor="right"
      open={isNotificationDrawerOpen}
      onClose={closeNotificationDrawer}
      sx={{
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
        },
      }}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="h2">
            Benachrichtigungen
          </Typography>
          <IconButton onClick={closeNotificationDrawer} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        
        {unreadCount > 0 && (
          <Box display="flex" gap={1} mt={1}>
            <Chip 
              label={`${unreadCount} ungelesen`} 
              color="primary" 
              size="small" 
            />
            {/*
            <Button
              variant={'contained'}
              size="small"
              startIcon={<CheckIcon />}
              onClick={handleMarkAllAsRead}
              sx={{ fontSize: '0.6rem' }}
            >
              Alle als gelesen markieren
            </Button>
            */}
          </Box>
        )}

        <Box display="flex" gap={1} mt={1}>
          <Button
            variant={showUnreadOnly ? 'outlined' : 'contained'}
            size="small"
            onClick={() => setShowUnreadOnly(false)}
          >
            Alle
          </Button>
          <Button
            variant={showUnreadOnly ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setShowUnreadOnly(true)}
          >
            Ungelesen
          </Button>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {loading && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box p={2}>
            <Alert severity="error">
              Fehler beim Laden der Benachrichtigungen: {error.message}
            </Alert>
          </Box>
        )}

        {!loading && !error && notifications.length === 0 && (
          <Box p={3} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              {showUnreadOnly ? 'Keine ungelesenen Benachrichtigungen' : 'Keine Benachrichtigungen vorhanden'}
            </Typography>
          </Box>
        )}

        <List>
          {notifications.map((notification, index) => (
            <React.Fragment key={notification.id}>
              <ListItem
                sx={{
                  backgroundColor: notification.isRead ? 'inherit' : 'action.hover',
                  '&:hover': {
                    backgroundColor: 'action.selected'
                  }
                }}
              >
                <Avatar sx={{ mr: 2, width: 40, height: 40 }}>
                  {getNotificationIcon(notification)}
                </Avatar>
                
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={notification.isRead ? 'normal' : 'bold'}
                      >
                        {getNotificationTitle(notification)}
                      </Typography>
                      {notification.updateType === 'MAJOR' && (
                        <Chip label="Wichtig" color="warning" size="small" />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {getNotificationDescription(notification)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatNotificationTime(notification.createdAt)}
                      </Typography>
                    </Box>
                  }
                />
                
                <ListItemSecondaryAction>
                  <Box display="flex" flexDirection="column" gap={0.5}>
                    {!notification.isRead && (
                      <Tooltip title="Als gelesen markieren">
                        <IconButton
                          size="small"
                          onClick={() => handleMarkAsRead(notification.id)}
                        >
                          <MarkReadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
              
              {index < notifications.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </Box>
    </Drawer>
  );
};
